const DEFAULT_TOOLS = ['dashboard', 'profile', 'version', 'fleetdata'];

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item).trim());
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeToolName = (tool) =>
  String(tool || '')
    .trim()
    .toLowerCase();

const extractPermissions = (value) =>
  normalizeList(value).filter((entry) => entry.includes('.'));

const isPowerAdmin = (user) =>
  Boolean(user && user.role === 'admin' && parseInt(user.privil, 10) === 1);

const hasRole = (user, expectedRoles = []) => {
  const allowedRoles = normalizeList(expectedRoles).map((role) =>
    role.toLowerCase()
  );

  if (allowedRoles.length < 1) return true;
  return allowedRoles.includes(String(user?.role || '').toLowerCase());
};

const getCurrentRole = (state) => {
  const currentUserRole = String(state.currentUser?.role || '').trim();
  if (!currentUserRole) return null;

  return (
    (state.userRoles || []).find(
      (role) =>
        String(role.rolename || '').toLowerCase() ===
        currentUserRole.toLowerCase()
    ) || null
  );
};

const getCurrentUserPermissions = (state) => {
  const currentPermissions = normalizeList(state.currentUserAllowedPermissions);
  if (currentPermissions.length > 0) {
    return Array.from(new Set(currentPermissions));
  }

  const role = getCurrentRole(state);
  const rolePermissions = extractPermissions(role?.permissions);
  const roleToolPermissions = extractPermissions(role?.toollist);
  const userPermissions = extractPermissions(state.currentUser?.permissions);
  const userToolPermissions = extractPermissions(state.currentUser?.toollist);

  return Array.from(
    new Set([
      ...rolePermissions,
      ...roleToolPermissions,
      ...userPermissions,
      ...userToolPermissions,
    ])
  );
};

const includesPermission = (permissions, permission) => {
  const permissionKey = String(permission || '').trim();
  if (!permissionKey) return false;

  return permissions.includes(permissionKey);
};

const hasPermission = (state, permission) => {
  if (isPowerAdmin(state.currentUser)) return true;
  const permissions = getCurrentUserPermissions(state);
  return includesPermission(permissions, permission);
};

const hasAnyPermission = (state, permissions = []) => {
  if (isPowerAdmin(state.currentUser)) return true;
  const availablePermissions = getCurrentUserPermissions(state);

  return normalizeList(permissions).some((permission) =>
    includesPermission(availablePermissions, permission)
  );
};

const hasAllPermissions = (state, permissions = []) => {
  if (isPowerAdmin(state.currentUser)) return true;

  const requiredPermissions = normalizeList(permissions);
  if (requiredPermissions.length < 1) return true;

  const availablePermissions = getCurrentUserPermissions(state);
  return requiredPermissions.every((permission) =>
    includesPermission(availablePermissions, permission)
  );
};

const canAccessTool = (state, tool) => {
  const requestedTool = normalizeToolName(tool);
  if (!requestedTool) return false;

  if (DEFAULT_TOOLS.includes(requestedTool)) return true;

  const allowedTools = normalizeList(state.currentUserAllowedTools).map(
    normalizeToolName
  );

  return allowedTools.includes(requestedTool);
};

export {
  DEFAULT_TOOLS,
  canAccessTool,
  getCurrentUserPermissions,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  hasRole,
  isPowerAdmin,
  normalizeList,
};
